import { Check } from "lucide-react";
import type { CSSProperties } from "react";

import { CcPhotoScene } from "@/components/services/shared/CcPhotoScene";
import { Mark } from "@/components/services/shared/demo-parts";

import type { AdvertisingDemoCopy } from "./advertising-demo-copy";

/**
 * The three "Värdet av rätt annonsering" tabs as real photos with cards for
 * the made-up outdoor-gear shop laid over them: every krona tracked, more
 * messages tested, profitable growth. Copy comes from `advertising-demo.json`.
 */

const delay = (d: number) => ({ "--d": d }) as CSSProperties;

function Bars({ series }: { series: number[] }) {
  const max = Math.max(...series);
  return (
    <ul className="cc-sv-visits__bars">
      {series.map((value, index) => (
        <li
          key={index}
          style={
            { "--h": `${(value / max) * 100}%`, "--i": index } as CSSProperties
          }
        />
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- 1. every krona tracked

export function AdTrackingPhotoView({
  copy,
}: {
  copy: AdvertisingDemoCopy["tracking"];
}) {
  return (
    <CcPhotoScene photo={copy.photo} variant="ad-tracking">
      <div className="cc-wv-card cc-adv-channels" style={delay(0)}>
        <p className="cc-wv-card__head">
          {copy.tableTitle}
          <em>{copy.tableMeta}</em>
        </p>
        <p className="cc-adv-kpi">
          <b>{copy.kpiValue}</b>
          <span>{copy.kpiPeriod}</span>
        </p>
        <small className="cc-adv-kpi__change">{copy.kpiChange}</small>
        <table className="cc-adv-table">
          <thead>
            <tr>
              <th scope="col">{copy.columns.channel}</th>
              <th scope="col">{copy.columns.spend}</th>
              <th scope="col">{copy.columns.roas}</th>
            </tr>
          </thead>
          <tbody>
            {copy.rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">
                  <Mark logo={row.logo} badge={row.badge} />
                  {row.label}
                </th>
                <td>{row.spend}</td>
                <td>
                  <b>{row.roas}</b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cc-wv-card cc-adv-setup" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.setupTitle}</p>
        <ul>
          {copy.setup.slice(0, 3).map((item) => (
            <li key={item.name}>
              <Check size={13} strokeWidth={2.6} />
              <span>{item.name}</span>
              <em data-tone={item.tone}>{item.status}</em>
            </li>
          ))}
        </ul>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 2. more messages tested

export function AdTestsPhotoView({
  copy,
}: {
  copy: AdvertisingDemoCopy["tests"];
}) {
  return (
    <CcPhotoScene photo={copy.photo} variant="ad-tests">
      <div className="cc-wv-card cc-adv-tests" style={delay(0)}>
        <p className="cc-wv-card__head">
          {copy.testsTitle}
          <em>{copy.testsMeta}</em>
        </p>
        <ul>
          {copy.rows.map((row) => (
            <li key={row.label}>
              <Mark logo={row.logo} badge={row.badge} />
              <span>{row.label}</span>
              <b>{row.ctr}</b>
              <em data-tone={row.tone}>{row.status}</em>
            </li>
          ))}
        </ul>
      </div>

      <div className="cc-wv-card cc-adv-count" style={delay(1)}>
        <p className="cc-adv-kpi">
          <b>{copy.kpiValue}</b>
          <span>{copy.kpiPeriod}</span>
        </p>
        <small className="cc-adv-kpi__change">{copy.kpiChange}</small>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 3. profitable growth

export function AdGrowthPhotoView({
  copy,
}: {
  copy: AdvertisingDemoCopy["growth"];
}) {
  return (
    <CcPhotoScene photo={copy.photo} variant="ad-growth">
      <div className="cc-wv-card cc-adv-revenue" style={delay(0)}>
        <p className="cc-wv-card__head">
          {copy.revenueTitle}
          <em>{copy.revenueMeta}</em>
        </p>
        <p className="cc-adv-kpi">
          <b>{copy.revenueValue}</b>
          <span>{copy.revenuePeriod}</span>
        </p>
        <Bars series={copy.revenueSeries} />
        <small className="cc-adv-kpi__change">{copy.revenueChange}</small>
      </div>

      <div className="cc-wv-card cc-adv-cpa" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.cpaTitle}</p>
        <p className="cc-adv-kpi">
          <b>{copy.cpaValue}</b>
          <span>{copy.cpaPeriod}</span>
        </p>
        <small className="cc-adv-kpi__change">{copy.cpaChange}</small>
      </div>
    </CcPhotoScene>
  );
}
